// src/components/products/ProductForm/helpers/formValidator.js

import toast from "react-hot-toast";
import { isTimeAfter, isEndDateAfterStartDate } from "../../../../utils/forms/dateTimeHelpers";
import { getProductTypeInfo } from "../../../../utils/forms/productFormHelpers";
import { TOUR_TYPES } from "./formConstants";

/**
 * ✅ Validate Step 0: Product Type Selection
 */
const validateStep0 = (formData, isEdit, isArabic) => {
    const errors = {};

    if (!isEdit && !formData.product_type_id) {
        errors.product_type_id = isArabic
            ? "يجب اختيار نوع المنتج"
            : "Product type is required";
    }

    return errors;
};

/**
 * ✅ Validate Step 1: General Info
 */
const validateStep1 = (formData, isEdit, isArabic) => {
    const errors = {};

    if (!formData.category_id) {
        errors.category_id = isArabic ? "التصنيف مطلوب" : "Category is required";
    }

    if (!formData.transportation_id) {
        errors.transportation_id = isArabic
            ? "وسيلة النقل مطلوبة"
            : "Transportation is required";
    }

    if (!formData.service_id) {
        errors.service_id = isArabic ? "الخدمة مطلوبة" : "Service is required";
    }

    if (!formData.pricing_unit_id) {
        errors.pricing_unit_id = isArabic
            ? "وحدة التسعير مطلوبة"
            : "Pricing unit is required";
    }

    // Validate Dates: End date > Start date
    if (formData.rate_availability_start_date && formData.rate_availability_end_date) {
        if (!isEndDateAfterStartDate(
            formData.rate_availability_start_date,
            formData.rate_availability_end_date
        )) {
            errors.rate_availability_end_date = isArabic
                ? "تاريخ النهاية يجب أن يكون بعد تاريخ البداية"
                : "End date must be after start date";
            toast.error(errors.rate_availability_end_date);
        }
    }

    if (!isEdit && !formData.cover_image) {
        errors.cover_image = isArabic
            ? "صورة الغلاف مطلوبة"
            : "Cover image is required";
    }

    return errors;
};

/**
 * ✅ Validate Transfer times
 */
const validateTransferTimes = (formData, isArabic) => {
    const errors = {};

    if (!formData.operating_hours_start_time) {
        errors.operating_hours_start_time = isArabic
            ? "وقت البداية مطلوب"
            : "Start time is required";
    }

    if (!formData.operating_hours_end_time) {
        errors.operating_hours_end_time = isArabic
            ? "وقت النهاية مطلوب"
            : "End time is required";
    }

    // Validate: Start time < End time
    if (formData.operating_hours_start_time && formData.operating_hours_end_time) {
        if (!isTimeAfter(formData.operating_hours_start_time, formData.operating_hours_end_time)) {
            errors.operating_hours_end_time = isArabic
                ? "وقت النهاية يجب أن يكون بعد وقت البداية"
                : "End time must be after start time";
            toast.error(errors.operating_hours_end_time);
        }
    }

    return errors;
};

/**
 * ✅ Validate Tour Default type times
 */
const validateTourDefaultTimes = (formData, isArabic) => {
    return validateTransferTimes(formData, isArabic); // Same validation
};

/**
 * ✅ Validate Tour Morning & Evening times
 */
const validateTourMorningEveningTimes = (formData, isArabic) => {
    const errors = {};

    // Morning times validation
    if (!formData.morning_operating_hours_start_time) {
        errors.morning_operating_hours_start_time = isArabic
            ? "وقت البداية الصباحي مطلوب"
            : "Morning start time is required";
    }

    if (!formData.morning_operating_hours_end_time) {
        errors.morning_operating_hours_end_time = isArabic
            ? "وقت النهاية الصباحي مطلوب"
            : "Morning end time is required";
    }

    // Validate: Morning Start < Morning End
    if (formData.morning_operating_hours_start_time && formData.morning_operating_hours_end_time) {
        if (!isTimeAfter(formData.morning_operating_hours_start_time, formData.morning_operating_hours_end_time)) {
            errors.morning_operating_hours_end_time = isArabic
                ? "وقت النهاية الصباحي يجب أن يكون بعد وقت البداية"
                : "Morning end time must be after start time";
            toast.error(errors.morning_operating_hours_end_time);
        }
    }

    // Evening times validation
    if (!formData.evening_operating_hours_start_time) {
        errors.evening_operating_hours_start_time = isArabic
            ? "وقت البداية المسائي مطلوب"
            : "Evening start time is required";
    }

    if (!formData.evening_operating_hours_end_time) {
        errors.evening_operating_hours_end_time = isArabic
            ? "وقت النهاية المسائي مطلوب"
            : "Evening end time is required";
    }

    // Validate: Evening Start < Evening End
    if (formData.evening_operating_hours_start_time && formData.evening_operating_hours_end_time) {
        if (!isTimeAfter(formData.evening_operating_hours_start_time, formData.evening_operating_hours_end_time)) {
            errors.evening_operating_hours_end_time = isArabic
                ? "وقت النهاية المسائي يجب أن يكون بعد وقت البداية"
                : "Evening end time must be after start time";
            toast.error(errors.evening_operating_hours_end_time);
        }
    }

    return errors;
};

/**
 * ✅ Validate Step 2: Product Details (Transfer/Tour)
 */
const validateStep2 = (formData, isArabic) => {
    const { isTransfer, isTour } = getProductTypeInfo(formData.selectedProductType);

    // Transfer Validation
    if (isTransfer) {
        return validateTransferTimes(formData, isArabic);
    }

    // Tour Validation
    if (isTour) {
        if (formData.type === TOUR_TYPES.DEFAULT) {
            return validateTourDefaultTimes(formData, isArabic);
        } else if (formData.type === TOUR_TYPES.MORNING_EVENING) {
            return validateTourMorningEveningTimes(formData, isArabic);
        }
    }

    return {};
};

/**
 * ✅ Main validation function
 */
export const validateStep = (step, formData, isEdit, isArabic) => {
    let errors = {};

    switch (step) {
        case 0:
            errors = validateStep0(formData, isEdit, isArabic);
            break;
        case 1:
            errors = validateStep1(formData, isEdit, isArabic);
            break;
        case 2:
            errors = validateStep2(formData, isArabic);
            break;
        default:
            break;
    }

    return errors;
};
